import type { FetchResponseI } from 'types';
import { fetchData } from 'Utils/fetch-data';

/** Entidad tal como la devuelve el backend en `data`. */
export interface InstructorApiModel {
  id: string;
  name: string;
  email: string;
  phone: string;
  organization: string;
  type: 'internal' | 'external';
  status: 'active' | 'inactive';
  createdAt: string;
  updatedAt: string;
}

/** Cuerpo estricto para crear/editar: solo estos campos (sin id ni timestamps). */
export interface InstructorPayload {
  name: string;
  email: string;
  phone: string;
  organization: string;
  type: 'internal' | 'external';
  status: 'active' | 'inactive';
}

/** Filtros de servidor para el listado. Todos opcionales. */
export interface InstructorListFilter {
  search?: string;
  status?: 'all' | 'active' | 'inactive';
  page?: number;
  pageSize?: number;
}

/** Conteos por estatus que devuelve `meta` (respetan `search`, ignoran `status`). */
export interface InstructorListMeta {
  total: number;
  active: number;
  inactive: number;
}

// `baseURL` ya incluye `/api`, así que aquí solo va el recurso.
const ENDPOINT = '/instructors';

/**
 * Cliente REST del catálogo de instructores. Usa `fetchData`, que adjunta el
 * token Bearer y lee el envelope `{ data }`. Los errores del backend llegan en
 * el campo `error` (mensaje) y `code` (p. ej. `email_taken` en un 409).
 *
 * `readAll` pide además `meta` (conteos) y `pagination` al servidor.
 */
const instructorsApi = {
  readAll: (filters?: InstructorListFilter): Promise<FetchResponseI<InstructorApiModel[]>> =>
    fetchData<InstructorApiModel[]>(ENDPOINT, {
      method: 'GET',
      params: filters,
      includeJsonApiMeta: true,
      includePagination: true,
    }),

  create: (payload: InstructorPayload): Promise<FetchResponseI<InstructorApiModel>> =>
    fetchData<InstructorApiModel>(ENDPOINT, { method: 'POST', data: payload }),

  update: (id: string, payload: InstructorPayload): Promise<FetchResponseI<InstructorApiModel>> =>
    fetchData<InstructorApiModel>(`${ENDPOINT}/${id}`, { method: 'PUT', data: payload }),

  remove: (id: string): Promise<FetchResponseI<boolean>> =>
    fetchData<boolean>(`${ENDPOINT}/${id}`, { method: 'DELETE' }),
};

export default instructorsApi;
